import type { ReactNode } from 'react';

// Minimal markdown renderer scoped to help content authored in this repo.
// Supports: # h1, ## h2, ### h3, paragraphs, - unordered lists, 1. ordered
// lists, **bold**, *italic*, `inline code`. Anything else falls through to
// plain text. We avoid a markdown library here because the help corpus is
// authored in-house and stays inside this constrained grammar.
// (Mirrors admin-portal/src/components/markdown.tsx — kept per-portal per the
// current convention; a shared @greatway/ui home is a future cleanup.)

interface Block {
  key: number;
  node: ReactNode;
}

export function renderMarkdown(source: string): ReactNode {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let listItems: string[] = [];
  let listKind: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (listItems.length === 0) return;
    const Tag = listKind === 'ol' ? 'ol' : 'ul';
    const className =
      listKind === 'ol'
        ? 'list-decimal pl-5 my-3 space-y-1.5 text-sm text-gray-700 leading-relaxed'
        : 'list-disc pl-5 my-3 space-y-1.5 text-sm text-gray-700 leading-relaxed';
    blocks.push({
      key: blocks.length,
      node: (
        <Tag className={className}>
          {listItems.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </Tag>
      ),
    });
    listItems = [];
    listKind = null;
  };

  for (const rawLine of lines) {
    const line = rawLine;

    if (line.startsWith('### ')) {
      flushList();
      blocks.push({
        key: blocks.length,
        node: <h3 className="text-sm font-semibold text-gray-900 mt-5 mb-2">{renderInline(line.slice(4))}</h3>,
      });
      continue;
    }
    if (line.startsWith('## ')) {
      flushList();
      blocks.push({
        key: blocks.length,
        node: (
          <h2 className="text-base font-semibold text-gray-900 mt-6 mb-3 border-b border-gray-100 pb-1">
            {renderInline(line.slice(3))}
          </h2>
        ),
      });
      continue;
    }
    if (line.startsWith('# ')) {
      flushList();
      blocks.push({
        key: blocks.length,
        node: <h1 className="text-lg font-semibold text-gray-900 mb-3">{renderInline(line.slice(2))}</h1>,
      });
      continue;
    }

    const ulMatch = line.match(/^- (.*)$/);
    if (ulMatch) {
      if (listKind && listKind !== 'ul') flushList();
      listKind = 'ul';
      listItems.push(ulMatch[1] ?? '');
      continue;
    }

    const olMatch = line.match(/^\d+\.\s+(.*)$/);
    if (olMatch) {
      if (listKind && listKind !== 'ol') flushList();
      listKind = 'ol';
      listItems.push(olMatch[1] ?? '');
      continue;
    }

    if (line.trim() === '') {
      flushList();
      continue;
    }

    flushList();
    blocks.push({
      key: blocks.length,
      node: <p className="text-sm text-gray-700 leading-relaxed my-2">{renderInline(line)}</p>,
    });
  }

  flushList();

  return blocks.map(({ key, node }) => <div key={key}>{node}</div>);
}

// Inline formatting: **bold**, *italic*, `code`. Tokens cannot nest.
function renderInline(text: string): ReactNode {
  const tokens: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) tokens.push(text.slice(lastIndex, match.index));
    const segment = match[0];
    if (segment.startsWith('**') && segment.endsWith('**')) {
      tokens.push(<strong key={key++} className="font-semibold text-gray-900">{segment.slice(2, -2)}</strong>);
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      tokens.push(<code key={key++} className="px-1 py-0.5 rounded bg-gray-100 text-gray-800 text-[0.85em] font-mono">{segment.slice(1, -1)}</code>);
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      tokens.push(<em key={key++} className="italic">{segment.slice(1, -1)}</em>);
    } else {
      tokens.push(segment);
    }
    lastIndex = match.index + segment.length;
  }

  if (lastIndex < text.length) tokens.push(text.slice(lastIndex));

  return tokens.length === 1 ? tokens[0] : <>{tokens}</>;
}
